import { FC, PointerEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useEditorData } from '../lib/FabricJs';
import { isTypingInField } from '../lib/FabricJs/hooks/useKeyboardEvents';

const PanLayer = styled.div<{ grabbing: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 5;
  cursor: ${({ grabbing }) => (grabbing ? 'grabbing' : 'grab')};
  touch-action: none;
`;

const getWorkspace = () => document.getElementById('canvas-editor-container');

// Holding Space turns the pointer into a hand that scrolls the zoomed slide;
// the layer keeps those clicks away from the canvas and widget overlays.
const SpacePan: FC = () => {
  const { canvas } = useEditorData();
  const [active, setActive] = useState(false);
  const [grabbing, setGrabbing] = useState(false);
  const start = useRef<{
    x: number;
    y: number;
    left: number;
    top: number;
  } | null>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code !== 'Space') return;
      const target = event.target as HTMLElement;
      // Space still presses a focused button and types in fields.
      if (
        isTypingInField(target) ||
        (target !== document.body &&
          !target.closest?.('#canvas-editor-container'))
      ) {
        return;
      }
      // @ts-ignore: isEditing only exists on text objects.
      if (canvas?.getActiveObject()?.isEditing) return;
      event.preventDefault();
      setActive(true);
    };
    const stop = () => {
      setActive(false);
      setGrabbing(false);
      start.current = null;
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') stop();
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', stop);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', stop);
    };
  }, [canvas]);

  if (!active) return null;

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    const workspace = getWorkspace();
    if (!workspace) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    start.current = {
      x: event.clientX,
      y: event.clientY,
      left: workspace.scrollLeft,
      top: workspace.scrollTop,
    };
    setGrabbing(true);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const workspace = getWorkspace();
    if (!start.current || !workspace) return;
    workspace.scrollLeft =
      start.current.left - (event.clientX - start.current.x);
    workspace.scrollTop = start.current.top - (event.clientY - start.current.y);
  };

  const onPointerUp = () => {
    start.current = null;
    setGrabbing(false);
  };

  return (
    <PanLayer
      grabbing={grabbing}
      aria-hidden="true"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    />
  );
};

export default SpacePan;
